import { SVButton, SVCheckbox, SVSummaryCard } from '@/components/core';
import ExternalAccountDetailsCards from '@/components/externalAccounts/details/ExternalAccountDetailsCards';
import ExternalAccountDocuments from '@/components/externalAccounts/details/ExternalAccountDocuments';
import { MESSAGES } from '@/constants/messages';
import { ROUTES } from '@/constants/routes';
import { useSubmitBankApplication } from '@/hooks/requests/useBankApplications';
import { useGetExternalAccountDetails } from '@/hooks/requests/useExternalAccounts';
import {
  BankApplicationAccountType,
  BankApplicationFormValues,
} from '@/types/bankApplications';
import {
  ExternalAccountListItem,
  ExternalBankAccountStatus,
} from '@/types/externalAccounts';
import { SelectOption } from '@/types/inputs';
import { formatCurrency } from '@/utils/currency';
import { titleCase } from '@/utils/stringCaser';
import showToast from '@/utils/toast';
import { ExpandMore } from '@mui/icons-material';
import {
  Stack,
  Box,
  useMediaQuery,
  Card,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

type BankAccountApplicationConfirmProps = {
  setShowConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  customerList: SelectOption[];
  teammatesList: SelectOption[];
  businessList: SelectOption[];
  externalAccounts: ExternalAccountListItem[];
};

export default function BankAccountApplicationConfirm({
  setShowConfirm,
  customerList,
  teammatesList,
  businessList,
}: BankAccountApplicationConfirmProps) {
  const navigate = useNavigate();
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const formMethods = useFormContext<BankApplicationFormValues>();
  const { watch, handleSubmit } = formMethods;
  const [
    accountType,
    applicationId,
    depositAmount,
    advisorId,
    businessId,
    individualOwnerId,
    jointOwnerIds,
    beneficiaries,
    externalAccountDepositId,
  ] = watch([
    'accountType',
    'applicationId',
    'depositAmount',
    'advisorId',
    'businessId',
    'individualOwnerId',
    'jointOwnerIds',
    'beneficiaries',
    'externalAccountDepositId',
  ]);
  const isIndividualAccount =
    accountType === BankApplicationAccountType.INDIVIDUAL;
  const isJointAccount = accountType === BankApplicationAccountType.JOINT;

  const { externalAccountDetails } = useGetExternalAccountDetails(
    externalAccountDepositId,
  );
  const { submitBankApplication, isSubmitBankApplicationPending } =
    useSubmitBankApplication();

  async function handleSubmitBankApplication() {
    try {
      await submitBankApplication(applicationId);
      showToast({
        type: 'success',
        data: {
          content:
            'The bank application has been successfully submitted and is now waiting on the owner(s) to approve it',
        },
      });
      navigate(`${ROUTES.BANK_ACCOUNT_APPLICATIONS}/${applicationId}`);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <Stack spacing={2}>
      <SVSummaryCard
        hideCardStyle
        label="Review Bank Application"
        items={[
          {
            label: 'Account Type',
            text: isIndividualAccount
              ? 'Individual'
              : isJointAccount
                ? 'Joint'
                : 'Business',
          },
          {
            label: 'Advisor',
            text:
              teammatesList.find(teammate => teammate.value === advisorId)
                ?.label || '',
          },
          {
            label: 'Owners',
            text: isIndividualAccount
              ? customerList.find(
                  customer => customer.value === individualOwnerId,
                )?.label || ''
              : isJointAccount
                ? customerList
                    .filter(item =>
                      jointOwnerIds.includes(item.value.toString()),
                    )
                    .map(owner => {
                      return owner.label;
                    })
                    .join(',\n')
                : '',
          },
          {
            label: 'Beneficiaries',
            text: beneficiaries.length
              ? beneficiaries
                  .map(beneficiary => {
                    return `${beneficiary.firstName} ${beneficiary.lastName} - ${beneficiary.percentOfAllocation}%`;
                  })
                  .join(',\n')
              : '',
          },
          {
            label: 'Business',
            text: businessList.find(business => business.value === businessId)
              ?.label,
          },
          {
            label: 'Initial Deposit From',
            text: externalAccountDetails?.bankName,
          },
          {
            label: 'Initial Deposit Amount',
            text: formatCurrency({ amount: depositAmount }),
          },
        ]}
      />
      {externalAccountDetails &&
        externalAccountDetails?.status !==
          ExternalBankAccountStatus.VERIFIED && (
          <Card>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header">
                <Typography variant="h6">
                  Verify {externalAccountDetails.bankName}{' '}
                  {titleCase(externalAccountDetails.category)} ...
                  {externalAccountDetails.accountNumberLast4}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {!externalAccountDetails.supportsWireTransfers && (
                    <Alert severity="warning">
                      This external account does not support wire funding
                    </Alert>
                  )}
                  <ExternalAccountDetailsCards
                    externalAccountDetails={externalAccountDetails}
                  />
                  <ExternalAccountDocuments
                    uuid={externalAccountDetails.uuid}
                    documents={externalAccountDetails.proofsOfOwnership}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
            <SVCheckbox
              name="verifiedExternalAccount"
              label="I have reviewed and verified this external account"
              rules={{ required: true }}
            />
          </Card>
        )}
      <SVCheckbox
        name="verified"
        label="I have reviewed and verified this application submission"
        rules={{ required: true }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column',
          gap: 2,
        }}>
        <SVButton onClick={() => setShowConfirm(false)} variant="tertiary">
          Back
        </SVButton>
        <SVButton
          isLoading={isSubmitBankApplicationPending}
          onClick={handleSubmit(handleSubmitBankApplication)}>
          Submit
        </SVButton>
      </Box>
    </Stack>
  );
}
